import React, { useContext, useEffect, useState } from "react";
import NavBar from "../../components/NavBar";
import SearchHashtags from "../../components/search/SearchHashtags";
import { useLocation } from "react-router-dom";
import { Filter } from "./Filter";
import FeedAdvertisement from "../../components/feeds/sidebar/FeedAdvertisement";
import { useInfiniteQuery } from "react-query";
import { getAllSearchAgainstKeywordApi } from "../../services/searchApis";
import { toast } from "react-toastify";
import InfiniteScroll from "react-infinite-scroller";
import { WebinarCard } from "../../components/search/WebinarResultComp";
import { Language, LoginUserData } from "../../App";
import { useSplashAds } from "../../hooks/useSplashAds";
import SearchResultCard from "../../components/search/SearchResultCard";
import { LuVideo, LuTv, LuSearch } from "react-icons/lu";

export default function WebinarsSearch() {
  const query = useLocation().search;
  const { lang } = useContext(Language);
  const keyword = decodeURIComponent(query.split("=")[1] || "");
  const { splashData } = useContext(LoginUserData);
  const { ads: splashAds, isReady: areSplashAdsReady } = useSplashAds();
  const [selectedSpecializations, setSelectedSpecializations] = useState([]);
  const [isRefetching, setIsRefetching] = useState(false);
  const [date, setDate] = useState(null);
  const [sortBy, setSortBy] = useState(null);

  const specialization = selectedSpecializations;
  const sort = sortBy;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage, refetch } =
    useInfiniteQuery({
      queryKey: ["search-webinars", keyword],
      queryFn: ({ pageParam = 1 }) =>
        getAllSearchAgainstKeywordApi({
          keyword,
          type: "webinars",
          pageParam,
          specialization,
          date,
          sort,
        }),
      enabled: !!keyword,
      getNextPageParam: (lastPage) => {
        const nextPage = lastPage.data.current_page + 1;
        return nextPage <= lastPage.data.last_page ? nextPage : undefined;
      },
      keepPreviousData: true,
      onError: (error) => {
        toast.error(error.message);
      },
      refetchOnWindowFocus: false,
    });

  const webinars = data?.pages?.flatMap((page) => page?.data?.data) || [];
  const leftAds = splashAds?.search_left || [];
  const rightAds = splashAds?.search_right || [];
  const hashTags = data?.pages?.flatMap((page) => page?.hash_tags);
  const storedValue = JSON.parse(localStorage.getItem("contest_obj"));

  useEffect(() => {
    setIsRefetching(true);
    refetch().finally(() => setIsRefetching(false));
  }, [selectedSpecializations, date, sort, refetch]);

  return (
    <div className="search-page-wrapper">
      <NavBar />
      <Filter
        title={lang?.webinars || "Webinars"}
        options={[
          {
            title: lang?.specialization || "Specialization",
            options: splashData?.home?.subcategories,
          },
          {
            title: lang?.sort_by || "Sort By",
            options: [lang?.newest_to_oldest || "Newest to Oldest", lang?.oldest_to_newest || "Oldest to Newest"],
          },
          { title: lang?.date || "Date", options: [] },
        ]}
        onSpecializationSelect={(specializations) =>
          setSelectedSpecializations(specializations ? specializations.map((spec) => spec.value) : [])
        }
        onSortBy={(sort) => setSortBy(sort)}
        onSelectDate={(date) => setDate(date)}
      />

      <div className="search-layout-container">
        {/* Left Column: Ads */}
        <div className="search-left-col">
          <FeedAdvertisement
            ads={leftAds}
            loading={!areSplashAdsReady}
            new_contest_obj={storedValue}
          />
        </div>

        {/* Center Column: Results */}
        <div className="search-main-col">
          <div className="search-results-header-inline">
            <h1 className="search-main-heading">
              <span>{lang?.webinars || "Webinars"}</span>
              {keyword && (
                <span className="search-keyword-badge">
                  <LuSearch size={14} />
                  <span>"{keyword}"</span>
                </span>
              )}
            </h1>
            {!isLoading && !isRefetching && webinars?.length > 0 && (
              <span className="search-results-summary">
                {webinars.length} {webinars.length === 1 ? (lang?.result || "result") : (lang?.results || "results")} {lang?.found || "found"}
              </span>
            )}
          </div>

          <WebinarsList
            loading={isLoading || isRefetching}
            data={webinars}
            fetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        </div>

        {/* Right Column: Hashtags & Ads */}
        <div className="search-right-col">
          <SearchHashtags data={hashTags} />
          <FeedAdvertisement
            ads={rightAds}
            loading={!areSplashAdsReady}
            new_contest_obj={storedValue}
          />
        </div>
      </div>
    </div>
  );
}

const WebinarsList = ({
  loading,
  data,
  fetchNextPage,
  hasNextPage,
  isFetchingNextPage,
}) => {
  const { lang } = useContext(Language);

  return (
    <SearchResultCard
      icon={<LuVideo />}
      title={lang?.webinars || "Webinars"}
      count={data?.length}
      seeAll={false}
      showHeader={false}
    >
      <InfiniteScroll
        pageStart={0}
        loadMore={() => {
          if (!isFetchingNextPage) {
            fetchNextPage();
          }
        }}
        hasMore={hasNextPage}
        loader={
          <div className="search-agenda-grid mt-3">
            {[1, 2].map((n) => (
              <div key={n} className="search-agenda-card-compact">
                <div className="search-agenda-thumb-wrap search-shimmer-box" style={{ width: "100%", height: "140px" }} />
                <div className="search-agenda-content">
                  <div className="search-shimmer-box mb-2" style={{ height: "16px", width: "80%" }} />
                  <div className="search-shimmer-box mb-3" style={{ height: "13px", width: "60%" }} />
                  <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                    <div className="d-flex align-items-center gap-2">
                      <div className="search-shimmer-box rounded-circle" style={{ width: 22, height: 22 }} />
                      <div className="search-shimmer-box" style={{ height: 11, width: 65 }} />
                    </div>
                    <div className="search-shimmer-box" style={{ height: 12, width: 60 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        }
      >
        {loading ? (
          <div className="search-agenda-grid">
            {[1, 2, 3, 4].map((n) => (
              <div key={n} className="search-agenda-card-compact">
                <div className="search-agenda-thumb-wrap search-shimmer-box" style={{ width: "100%", height: "140px" }} />
                <div className="search-agenda-content">
                  <div className="search-shimmer-box mb-2" style={{ height: "16px", width: "80%" }} />
                  <div className="search-shimmer-box mb-3" style={{ height: "13px", width: "60%" }} />
                  <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                    <div className="d-flex align-items-center gap-2">
                      <div className="search-shimmer-box rounded-circle" style={{ width: 22, height: 22 }} />
                      <div className="search-shimmer-box" style={{ height: 11, width: 65 }} />
                    </div>
                    <div className="search-shimmer-box" style={{ height: 12, width: 60 }} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : data && data.length > 0 ? (
          <div className="search-agenda-grid">
            {data.map((webinar, index) => (
              <WebinarCard key={webinar?.id || index} item={webinar} />
            ))}
          </div>
        ) : (
          <div className="search-empty-state-card">
            <div className="search-empty-icon-wrap">
              <LuTv />
            </div>
            <h5 className="search-empty-title">{lang?.no_webinars_found || "No webinars found"}</h5>
            <p className="search-empty-desc">
              {lang?.try_different_keyword || "Try a different keyword or check spelling."}
            </p>
          </div>
        )}
      </InfiniteScroll>
    </SearchResultCard>
  );
};
